import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

interface StorageInfo {
  current_plan: {
    id: string;
    name: string;
    max_resumes?: number;
    storage_mb?: number; // Legacy field
    description: string;
  };
  usage: {
    resume_count?: number;
    usage_percentage: number;
    remaining_resumes?: number;
    is_unlimited?: boolean;
    // Legacy storage fields for backward compatibility
    total_size_bytes: number;
    total_size_mb: number;
    file_count: number;
    remaining_bytes?: number;
    remaining_mb?: number;
  };
  limits: {
    resume_limit?: number;
    is_unlimited?: boolean;
    // Legacy storage fields for backward compatibility
    storage_limit_bytes: number;
    storage_limit_mb: number;
  };
}

const getSafePercentage = (percentage: number | undefined): number => {
  if (typeof percentage !== 'number' || !Number.isFinite(percentage)) {
    return 0;
  }

  return Math.min(Math.max(percentage, 0), 100);
};

const UploadStorageUsage: React.FC = () => {
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStorageInfo();
  }, []);

  const loadStorageInfo = async () => {
    try {
      setLoading(true);
      const data = await apiService.getStorageInfo();
      setStorageInfo(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load storage information');
    } finally {
      setLoading(false);
    }
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-red-600';
    if (percentage >= 75) return 'bg-orange-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-indigo-600';
  };

  const getTextColor = (percentage: number): string => {
    if (percentage >= 90) return 'text-red-100';
    if (percentage >= 75) return 'text-orange-100';
    if (percentage >= 50) return 'text-yellow-100';
    return 'text-indigo-100';
  };

  const getBgColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-gradient-to-br from-red-400 to-red-500';
    if (percentage >= 75) return 'bg-gradient-to-br from-orange-400 to-red-400';
    if (percentage >= 50) return 'bg-gradient-to-br from-yellow-400 to-orange-400';
    if (percentage >= 25) return 'bg-gradient-to-br from-indigo-400 to-indigo-500';
    return 'bg-gradient-to-br from-indigo-500 to-indigo-600';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="h-3 bg-gray-200 rounded w-32 mb-2"></div>
          <div className="h-2 bg-gray-200 rounded mb-3"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center text-red-600">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  if (!storageInfo) return null;

  const { usage, limits, current_plan } = storageInfo;
  const usagePercentage = getSafePercentage(usage.usage_percentage);
  const resumeCount = usage.resume_count ?? usage.file_count ?? 0;
  const resumeLimit = limits.resume_limit;
  const isUnlimited = usage.is_unlimited;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <svg className="w-6 h-6 mr-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 00-1 1v6a1 1 0 001 1v1a2 2 0 01-2-2V5zM14 5a2 2 0 012 2v6a2 2 0 01-2 2v-1a1 1 0 001-1V6a1 1 0 00-1-1V5z" clipRule="evenodd" />
            <path d="M5 8a1 1 0 011-1h6a1 1 0 110 2H6a1 1 0 01-1-1zM5 10a1 1 0 011-1h6a1 1 0 110 2H6a1 1 0 01-1-1zM5 12a1 1 0 011-1h6a1 1 0 110 2H6a1 1 0 01-1-1z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900">Company Resume Usage</h3>
        </div>
        <span className="text-sm font-medium bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full">
          {current_plan.name}
        </span>
      </div>

      {/* Usage Display */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium text-gray-900">
            {resumeCount} resumes used {!isUnlimited ? `of ${resumeLimit}` : '(unlimited)'}
          </span>
          <span className="font-bold text-gray-600">
            {isUnlimited ? 'Unlimited' : `${usagePercentage.toFixed(1)}%`}
          </span>
        </div>
        {!isUnlimited && (
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${getUsageColor(usagePercentage)}`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            ></div>
          </div>
        )}
      </div>

      {/* Resume Stats */}
      <div className="grid grid-cols-3 gap-4 text-sm">
        {!isUnlimited ? (
          <>
{/*             
            <div className="text-center">
              <p className="font-semibold text-gray-900">
                {resumeCount}
              </p>
              <p className="text-xs text-gray-500">Used</p>
            </div> */}
          
          </>
        ) : (
          <div className="col-span-3 text-center">
            <p className="font-semibold text-gray-900">
              {resumeCount} resumes uploaded
            </p>
            <p className="text-xs text-gray-500">Unlimited Plan - No Restrictions</p>
          </div>
        )}
      </div>

      {/* Warning Messages */}
      {!isUnlimited && usagePercentage >= 95 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
          <div className="flex items-center text-red-700">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>Resume limit almost reached! Upgrade to continue uploading.</span>
          </div>
        </div>
      )}

      {!isUnlimited && usagePercentage >= 85 && usagePercentage < 95 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
          <div className="flex items-center text-yellow-700">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>Resume limit is {usagePercentage.toFixed(1)}% full. Consider upgrading soon.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadStorageUsage; 
